import { Fragment, useState } from 'react';
import {
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, AccountCircle as AccountCircleIcon } from '@mui/icons-material';
import { AgencyEmail } from '../../utils/agencyApi';

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

interface RemoveDialogProps {
  email: string | null;
  onConfirm: (email: string) => void;
  onClose: () => void;
}

const RemoveDialog = ({ email, onConfirm, onClose }: RemoveDialogProps) => (
  <Dialog open={!!email} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Remove {email}?</DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        This will permanently remove their access to your agency. They will no longer be able to log in.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" color="error" onClick={() => { if (email) { onConfirm(email); onClose(); } }}>
        Remove
      </Button>
    </DialogActions>
  </Dialog>
);

const AvatarRowView = ({ emails, onRemove }: { emails: AgencyEmail[]; onRemove: (e: string) => void }) => {
  const [pending, setPending] = useState<string | null>(null);
  if (emails.length === 0) return <Typography variant="body2" color="text.secondary">No authorized reporters yet.</Typography>;
  return (
    <>
      <List disablePadding>
        {emails.map((e, i) => (
          <Fragment key={e.id}>
            {i > 0 && <Divider />}
            <ListItem
              sx={{ py: 1, px: 2 }}
              secondaryAction={
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => setPending(e.email)} sx={{ mr: -0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <AccountCircleIcon sx={{ width: 36, height: 36, mr: 1.5, color: 'primary.main' }} />
              <ListItemText
                primary={<Typography variant="body2">{e.email}</Typography>}
                secondary={<Typography variant="caption" color="text.secondary">Added {formatDate(e.createdAt)}</Typography>}
              />
            </ListItem>
          </Fragment>
        ))}
      </List>
      <RemoveDialog email={pending} onConfirm={onRemove} onClose={() => setPending(null)} />
    </>
  );
};

export default AvatarRowView;
